import axios from "axios";

const RETRYABLE_STATUS_CODES = new Set([502, 503, 504]);
const RETRYABLE_METHODS = new Set(["get", "head", "options"]);
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 500;

declare global {
  interface Window {
    __APP_CONFIG__?: {
      REACT_APP_API_BASE_URL?: string;
      REACT_APP_GOOGLE_CLIENT_ID?: string;
      REACT_APP_GOOGLE_MAPS_API_KEY?: string;
    };
  }
}

const DEFAULT_PRODUCTION_API_BASE_URL = "/api";

const getDefaultApiBaseUrl = () => {
  const hostname = window.location.hostname;
  const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1";

  return isLocalhost ? "http://localhost:8080/api" : DEFAULT_PRODUCTION_API_BASE_URL;
};

const api = axios.create({
  baseURL:
    window.__APP_CONFIG__?.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_API_BASE_URL ||
    getDefaultApiBaseUrl(),
  timeout: 15000,
});

api.interceptors.request.use((config) => {
  const token = window.localStorage.getItem("eldoo-token");
  const existingHeader =
    typeof config.headers?.Authorization === "string"
      ? config.headers.Authorization
      : typeof config.headers?.authorization === "string"
      ? config.headers.authorization
      : null;
  const authorizationHeader =
    existingHeader ||
    (typeof api.defaults.headers.common.Authorization === "string"
      ? api.defaults.headers.common.Authorization
      : null) ||
    (token ? `Bearer ${token}` : null);

  if (authorizationHeader) {
    if (typeof config.headers?.set === "function") {
      config.headers.set("Authorization", authorizationHeader);
    } else {
      config.headers = {
        ...(config.headers || {}),
        Authorization: authorizationHeader,
      };
    }
    config._sentWithAuth = true;
    return config;
  }

  config._sentWithAuth = false;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    const status = error.response?.status;
    const method = String(config?.method || "get").toLowerCase();

    if (
      config &&
      RETRYABLE_METHODS.has(method) &&
      RETRYABLE_STATUS_CODES.has(status) &&
      (config._retryCount ?? 0) < MAX_RETRIES
    ) {
      config._retryCount = (config._retryCount ?? 0) + 1;
      await new Promise((resolve) => window.setTimeout(resolve, RETRY_DELAY_MS * config._retryCount));
      return api.request(config);
    }

    return Promise.reject(error);
  },
);

export const setApiAuthToken = (token: string | null) => {
  if (token) {
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
    return;
  }

  delete api.defaults.headers.common.Authorization;
};

export const resolveApiUrl = (path: string) => {
  const baseUrl = String(api.defaults.baseURL || DEFAULT_PRODUCTION_API_BASE_URL).replace(/\/$/, "");
  return `${baseUrl}${path.startsWith("/") ? path : `/${path}`}`;
};

export default api;
